/**
 * interpretation/hooks — TwilioHelper ↔ InterpretationController 연결점.
 *
 * TwilioHelper(통화 코어)가 컨트롤러를 직접 import 하면 순환 참조가 생기므로,
 * 양쪽이 이 얇은 레지스트리만 참조한다. 훅이 비어 있으면(통역 미사용) 전부 no-op —
 * 기존 통화 동작에 대한 행동 중립성이 보장된다.
 */
import type { RemoteAudioTrack, RemoteDataTrack } from 'twilio-video';

/** 통역 오디오 트랙 이름 접두사 — N자 확장 불변 계약 (설계 §14). 무거운 의존이 없는
 *  이 모듈에 두어 TwilioHelper(운영 번들)가 통역 스택 전체를 당기지 않게 한다. */
export const ITP_TRACK_PREFIX = 'itp-';
/** 자막 DataTrack 이름 (설계 §8.1) */
export const ITP_CC_TRACK = 'itp-cc';

export interface ItpHooks {
  /** 원격 오디오 트랙 attach 직후 — 재생 정책(itp=1.0 / 원음 ducking) 적용 지점 (설계 §4.3) */
  onRemoteAudio?: (track: RemoteAudioTrack, el: HTMLAudioElement, participantIdentity: string) => void;
  /** 원격 오디오 트랙 해제 — 정책 상태 정리 */
  onRemoteAudioGone?: (track: RemoteAudioTrack) => void;
  /** 원격 DataTrack 구독 — 자막 채널(itp-cc) 수신 연결 (설계 §8.1) */
  onRemoteData?: (track: RemoteDataTrack, participantIdentity: string) => void;
  /** 로컬 음소거 변경 — 통역 입력(clone 트랙)도 함께 제어 (설계 §4.0 이중 제어) */
  onLocalMute?: (enabled: boolean) => void;
  /** 통화 종료 — 통역 세션·트랙 전체 정리 */
  onCallLeft?: () => void;
  /**
   * Twilio disconnect **직전**에 await 되는 훅 — 회의록 녹음을 마감할 마지막 기회.
   * disconnect 가 먼저 실행되면 마이크 트랙이 죽어 MediaRecorder 가 빈 blob 을 내고
   * 녹음이 통째로 유실된다(실측: buildMinutes '녹음 대기 0/2'). 짧게 유지할 것.
   */
  onBeforeDisconnect?: () => Promise<void>;
}

export const itpHooks: ItpHooks = {};

/**
 * 통화 종료 의도 — CallHeader 의 종료 버튼·새로고침(beforeunload)·언마운트가 모두
 * 같은 twil.leaveRoom() 을 호출하므로, "사용자가 정말 끝냈는가"를 여기서 구분한다.
 * true 일 때만 링크를 폐기(서버 endItpCall)하고, 새로고침·이탈은 통화를 살려둬
 * 내 채널에서 재입장할 수 있게 한다.
 *
 * 이 얇은 모듈에 두는 이유: CallHeader(운영 번들)가 통역 스택 전체(@google/genai)를
 * 정적 import 로 끌어오면 메인 번들이 커진다 — ITP_TRACK_PREFIX 와 같은 이유.
 */
export const itpCallIntent = { userEnded: false };

/** 통화 링크 참가자 표시 정보 (identity → 이름·언어·음소거) */
export interface ItpParticipantInfo { name: string; lang?: string; muted?: boolean }

/**
 * 참가자 표시 레지스트리 — CallHeader(운영 번들)가 통역 스택(@google/genai 포함)을
 * 정적 import 하지 않고도 이름·언어를 읽기 위한 얇은 저장소.
 *
 * 왜 필요한가: 통화 링크의 participant identity 는 host-/guest- 접두 문자열이라
 * users 문서가 없어 ParticipantItem 의 프로필 조회가 실패한다(타일 미표시).
 * 이름·언어는 ItpCallCore 가 meta 로 이미 알고 있으므로 여기에 실어 공유한다.
 */
export const itpParticipants = {
  map: new Map<string, ItpParticipantInfo>(),
  listeners: new Set<() => void>(),
  emit() { this.listeners.forEach((l) => l()); },
  set(identity: string, info: ItpParticipantInfo) {
    const prev = this.map.get(identity);
    if (prev && prev.name === info.name && prev.lang === info.lang && prev.muted === info.muted) return;
    this.map.set(identity, info);
    this.emit();
  },
  remove(identity: string) { if (this.map.delete(identity)) this.emit(); },
  clear() { if (this.map.size) { this.map.clear(); this.emit(); } },
  subscribe(l: () => void) { this.listeners.add(l); return () => { this.listeners.delete(l); }; },
};
